import React, {useState} from 'react';
import { useMsal } from '@azure/msal-react';
import { TEAMS_LIST, VIEW_ALL_PLANNED, VIEW_ACTIVE_AND_CURRENT } from '../Common/Constants';
import { AzureDevOpsService, ProjectWorkItemData } from '../services/AzureDevOpsService';
import { PublicClientApplication } from '@azure/msal-browser';
import { Ticket } from '../services/Ticket';
import TimelineAdo from '../Timeline/Timeline';
import ToolBarAdo, {OptionType} from '../Timeline/Toolbar/ToolBar';
import { createAssigneeList } from '../utils/PrepareTicket';
import { PrepareTimelineDataAdoSimplified } from '../Timeline/data/PrepareTimelineData';
import dayjs from 'dayjs';
import { TelemetryService } from '../services/TelemetryService';


// Simple loading spinner component
const LoadingSpinner: React.FC = () => (
  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginTop: 40 }}>
    <div style={{
      border: '8px solid #f3f3f3',
      borderTop: '8px solid #3498db',
      borderRadius: '50%',
      width: 60,
      height: 60,
      animation: 'spin 1s linear infinite',
    }} />
    <span style={{ marginTop: 16, fontSize: 18, color: '#555' }}>Loading...</span>
    <style>{`
      @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }
    `}</style>
  </div>
);


interface TeamPlanningPageProps {
  useAdvancedMode: boolean;
  projectWorkItemData: ProjectWorkItemData[];
  usersData: any[];
  capexOptions: string[];
  govProjectOptions: string[];
  priorityDriverOptions: string[];
  demoOptions: string[];
  iterationsData: any[];
  productOptions: string[];
  investmentClassOptions: string[];
  selectedPriorityDriver: string;
  setSelectedPriorityDriver: (value: string) => void;
}

const TeamPlanningPage: React.FC<TeamPlanningPageProps> = (props) => {
    const { instance, accounts } = useMsal();
    const [tickets, setTickets] = useState<Ticket[] | null>(null);
    const [isLoaded, setIsLoaded] = useState(false);
    const [showChartMenu, setShowChartMenu] = useState(false);
    const [refresh, setRefresh] = useState<Date | null>()
    const [lastRefreshTime, setLastRefreshTime] = useState<number>(0);
    const [viewId, setViewId] = useState<string>(VIEW_ACTIVE_AND_CURRENT);
    const [quarterFilter, setQuarterFilter] = useState<any>(null);
    const [assigneeId, setAssigneeId] = useState<string>(localStorage.getItem("TeamPlanning-assigneeId") || "");  
    const projects = TEAMS_LIST.map((item) => item.project).filter((project) => project).sort();
    const [selectedProject, setSelectedProject] = useState<string>(localStorage.getItem("TeamPlanning-selectedProject") || "");
    const [selectedAreaPath, setSelectedAreaPath] = useState<string>(localStorage.getItem("TeamPlanning-selectedAreaPath") || "");
    const [areaPathList, setAreaPathList] = useState<OptionType[]>([{ value: "", label: "All" }]);
    const [multiSelectMode, setMultiSelectMode] = useState<boolean>(false);
    const [selectedTicketsCount, setSelectedTicketsCount] = useState<number>(0);
    const [onEditMultiSelect, setOnEditMultiSelect] = useState<(() => void) | null>(null);

    React.useEffect(() => {
      if (!multiSelectMode) {
        setSelectedTicketsCount(0);
        setOnEditMultiSelect(null);
      }
    }, [multiSelectMode]);
  
    const devOpsService = React.useMemo(() => 
      accounts.length > 0 ? new AzureDevOpsService(instance as PublicClientApplication, accounts[0]) : null,
      [instance, accounts]
    );
  
    const wiql = "Select [System.Id] , [System.Title], [System.State], [Custom.EndIteration], [Custom.StartIteration] From WorkItems Where [Custom.EndIteration] <> '' AND [State] <> 'Closed' and [System.TeamProject] = @project order by [Custom.PlanningPriority] asc, [Custom.StartIteration] asc, [System.CreatedDate] asc";
  

  React.useEffect(() => {
    document.title = "Team Planning: " + selectedProject;
    TelemetryService.trackPageView('TeamPlanningPage', {
      selectedProject,
      selectedPriorityDriver: props.selectedPriorityDriver,
      viewId
    });
    const fetchTickets = async () => {
      if (accounts.length > 0) {
        try {
          const devOpsService = new AzureDevOpsService(instance as PublicClientApplication, accounts[0]);
          const organization = 'sensio';
          const project = selectedProject;
          const result = await devOpsService.fetchTicketsByQuery(organization, project, wiql);

          if (result && Array.isArray(result.workItems) && result.workItems.length > 0) {
            // Parallelize fetching ticket details
            const ticketDetails: Ticket[] = await Promise.all(
              result.workItems.map(async (item: any) => {
                if (item.url) {
                  try {
                    const detail = await devOpsService.fetchWorkItemByUrl(item.url);
                    if (detail && detail.id) {
                      // TODO: Add all children
                      const _ticket : Ticket = new Ticket(detail);
                      _ticket.children = [];


                      if (_ticket.childrenIds && _ticket.childrenIds.length > 0) {
                        const childrenDetails = await Promise.all(
                          _ticket.childrenIds.map(async (childId: number) => {
                            try {
                              const resultChild = await devOpsService.fetchWorkItemById(childId);
                              const _ticketChild = new Ticket(resultChild, _ticket.initiative);
                              return _ticketChild;
                            } catch (err) {
                              console.error("Error fetching child ticket:", err);
                              return null;
                            }
                          })
                        );
                        _ticket.children = childrenDetails.filter((child): child is Ticket => child !== null);
                      }

                      return _ticket;
                    }
                  } catch (err) {
                    console.error("Error fetching:", err);
                  }
                }
                return null;
              })
            );
            // Filter out any nulls (failed fetches)
            const allTickets = ticketDetails.filter((t): t is Ticket => t !== null);

            // Add all children to the ticketDetails array
            const allChildren : Ticket[] = allTickets.flatMap(ticket => ticket.children || []);
            const uniqueTickets : Ticket[] = [...allTickets, ...allChildren].reduce((acc, ticket) => {
              if (!acc.some((t: Ticket) => t.id === ticket.id)) {
              acc.push(ticket);
              }
              return acc;
            }, [] as Ticket[]);
            setTickets(uniqueTickets.filter((t): t is Ticket => t !== null));
            
            setIsLoaded(true);
          } else {
            setTickets([]);
            setIsLoaded(true);
          }
        } catch (error) {
          console.error("Error fetching tickets:", error);
          setTickets([]);
          setIsLoaded(true);
        }
      }
    };
    fetchTickets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [instance, accounts, selectedProject, refresh]);

  const convertLegacyQuarterToDate = (quarterString: string): any => {
    const match = quarterString.match(/^(\d{2})Q(\d)$/);
    if (!match) return null;
    
    const year = 2000 + parseInt(match[1]);
    const quarter = parseInt(match[2]);
    
    const month = (quarter - 1) * 3;
    
    return dayjs().year(year).month(month).startOf('quarter' as any);
  };

  const getFilteredAssigneeList = () => {
    if (!tickets) return [];
    
    const timelineData = PrepareTimelineDataAdoSimplified(
      tickets, 
      "", // Empty teamId for team planning
      props.selectedPriorityDriver, 
      "", // Empty assigneeId to get all assignees
      viewId, 
      quarterFilter
    );
    
    return createAssigneeList(timelineData.filteredTickets);
  };
  
  const assigneeList = getFilteredAssigneeList();

  React.useEffect(() => {
    const storedViewId = localStorage.getItem("TeamPlanning-viewId");
    const storedQuarterFilter = localStorage.getItem("TeamPlanning-quarterFilter");
    if (storedViewId) {
      setViewId(storedViewId);
    }

    if (storedQuarterFilter && storedQuarterFilter !== "All" && storedQuarterFilter !== "") {
      const parsedDate = dayjs(storedQuarterFilter);
      if (parsedDate.isValid()) {
        setQuarterFilter(parsedDate);
      } else {
        const legacyQuarter = convertLegacyQuarterToDate(storedQuarterFilter);
        if (legacyQuarter) {
          setQuarterFilter(legacyQuarter);
        }
      }
    }
  }, []);

  React.useEffect(() => {
    try {
      localStorage.setItem("TeamPlanning-viewId", viewId);
    } catch (storageError) {
      console.warn('Failed to save TeamPlanning-viewId due to storage quota:', storageError);
    }
  }, [viewId]);

  React.useEffect(() => {
    try {
      localStorage.setItem("TeamPlanning-quarterFilter", quarterFilter ? quarterFilter.toISOString() : "");
    } catch (storageError) {
      console.warn('Failed to save TeamPlanning-quarterFilter due to storage quota:', storageError);
    }
  }, [quarterFilter]);

  React.useEffect(() => {
    try {
      localStorage.setItem("TeamPlanning-selectedAreaPath", selectedAreaPath);
    } catch (storageError) {
      console.warn('Failed to save TeamPlanning-selectedAreaPath due to storage quota:', storageError);
    }
  }, [selectedAreaPath]);

  // Fetch area paths from Azure DevOps when project changes
  React.useEffect(() => {
    const fetchAreaPaths = async () => {
      if (!devOpsService || !selectedProject) {
        setAreaPathList([{ value: "", label: "All" }]);
        return;
      }
      
      try {
        const organization = 'sensio';
        const areaPathsData = await devOpsService.fetchAreaPaths(organization, selectedProject);
        
        if (areaPathsData && areaPathsData.children) {
          const buildAreaPathOptions = (node: any, prefix: string = ""): OptionType[] => {
            const options: OptionType[] = [];
            if (node.children && node.children.length > 0) {
              node.children.forEach((child: any) => {
                const pathName = child.name;
                const fullPath = prefix ? `${prefix}\\${pathName}` : pathName;
                options.push({ value: fullPath, label: fullPath });
                options.push(...buildAreaPathOptions(child, fullPath));
              });
            }
            return options;
          };
          
          const paths = buildAreaPathOptions(areaPathsData);
          const unsortedList = [
            { value: "", label: "All" },
            { value: "__ROOT__", label: `${selectedProject} (Root)` },
            ...paths
          ];
          
          // Sort: keep "All" and Root at top, then sort rest alphabetically
          unsortedList.sort((a, b) => {
            if (a.value === "") return -1;
            if (b.value === "") return 1;
            if (a.value === "__ROOT__") return -1;
            if (b.value === "__ROOT__") return 1;
            return a.label.localeCompare(b.label);
          });
          
          setAreaPathList(unsortedList);
          
          // Clear selected area path if it's no longer available in the new project
          if (selectedAreaPath && !unsortedList.some(item => item.value === selectedAreaPath)) {
            setSelectedAreaPath("");
          }
        }
      } catch (error) {
        console.error("Error fetching area paths:", error);
        setAreaPathList([{ value: "", label: "All" }]);
      }
    };
    
    fetchAreaPaths();
  }, [devOpsService, selectedProject]);

  if (isLoaded) {

    
    return (
      <>
      <ToolBarAdo
        // Data
        projects={projects.map((project) => ({ value: project, label: project }))}
        initiativeList={[{label:"hei", value: "hoi"}]}
        assigneeList={assigneeList}
        priorityDriverOptions={props.priorityDriverOptions}
        tagList={[]}
        areaPathList={areaPathList}

        // Filters
        teamsId={""}
        quarterFilter={quarterFilter}
        viewId={viewId}
        selectedProject={selectedProject}
        selectedInitiative={""}
        assigneeId={assigneeId}
        selectedPriorityDriver={props.selectedPriorityDriver}
        selectedTags={[]}
        selectedAreaPath={selectedAreaPath}
        filterMinPlanningPri={-999999999}
        filterMaxPlanningPri={9999999999}
        includeChildren={true}
        // Filter callbacks
        setFilterMinPlanningPri= {(min: number) => {return}}
        setFilterMaxPlanningPri= {(max: number) => {return}}
        setQuarterFilter={setQuarterFilter}
        setTeamsId={() => {}}
        setSelectedPriorityDriver={props.setSelectedPriorityDriver}
        setSelectedTags={() => {}}
        setSelectedAreaPath={(value: string) => {
          setSelectedAreaPath(value);
        }}
        setIncludeChildren={(value: boolean) => {
        }}

        // Chart visuals
        chartHeight={1300}
        onHandleChartHeightChange={(value: number | null) => {return}}
        setOpenOnClickStatus= {(status: boolean) => {return}}
        openOnClickStatus={true}
        showTeamsFilter={false}
        useAdvancedMode={props.useAdvancedMode} // Pass useAdvanced
        multiSelectMode={multiSelectMode}
        onMultiSelectModeChange={setMultiSelectMode}
        selectedTicketsCount={selectedTicketsCount}
        onEditMultiSelect={() => onEditMultiSelect && onEditMultiSelect()}

        // Callbacks
        projectOnChange={(option: OptionType | null) => {
          const project = option ? option.value : "";
          setIsLoaded(false); // Show loading spinner when switching projects
          setSelectedProject(project);
          try {
            localStorage.setItem("TeamPlanning-selectedProject", project);
          } catch (storageError) {
            console.warn('Failed to save TeamPlanning-selectedProject due to storage quota:', storageError);
          }
          return}}
        onHideProject= {(project: string) => {return}}
        initiaitveOnChange={(option: OptionType | null) => {return}}
        viewOnChange={(option: OptionType | null) => {
          if (option) {
            setViewId(option.value);
          }
        }}
        assigneeOnChange={(option: OptionType | null) => {
          const value = option ? option.value : "";
          setAssigneeId(value);
          try {
            localStorage.setItem("TeamPlanning-assigneeId", value);
          } catch (storageError) {
            console.warn('Failed to save TeamPlanning-assigneeId due to storage quota:', storageError);
          }
        }}
        onRefreshClick= {() => {
          const now = Date.now();
          const timeSinceLastRefresh = now - lastRefreshTime;
          
          // If refresh clicked within 2 seconds, clear all caches
          if (timeSinceLastRefresh < 2000) {
            console.log("Double refresh detected - clearing all caches");
            // Clear area path cache
            localStorage.removeItem("TeamPlanning-areaPathsCache");
            localStorage.removeItem("TeamPlanning-areaPathsCacheTime");
            // Clear AuthenticatedApp caches
            localStorage.removeItem("workItemData");
            localStorage.removeItem("workItemDataTimestamp");
            localStorage.removeItem("usersData");
            localStorage.removeItem("usersDataTimestamp");
            localStorage.removeItem("capexData");
            localStorage.removeItem("capexDataTimestamp");
            localStorage.removeItem("govProjectData");
            localStorage.removeItem("govProjectDataTimestamp");
            localStorage.removeItem("priorityDriverData");
            localStorage.removeItem("priorityDriverDataTimestamp");
            localStorage.removeItem("demoData");
            localStorage.removeItem("demoDataTimestamp");
            localStorage.removeItem("initiativeData");
            localStorage.removeItem("initiativeDataTimestamp");
            localStorage.removeItem("productData");
            localStorage.removeItem("productDataTimestamp");
            localStorage.removeItem("investmentClassData");
            localStorage.removeItem("investmentClassDataTimestamp");
            localStorage.removeItem("iterationsData");
            localStorage.removeItem("iterationsDataTimestamp");
            window.location.reload();
          } else {
            const d : Date = new Date() 
            setRefresh(d);
            setLastRefreshTime(now);
          }
        }}
        onLogout= {() => {return}}
      />
        <TimelineAdo
          items={tickets || []}
          showChartMenu={showChartMenu}
          setShowChartMenu={setShowChartMenu}
          viewId={viewId || VIEW_ALL_PLANNED}
          quarterFilter={quarterFilter}
          selectedPriorityDriver={props.selectedPriorityDriver || ""}
          selectedTags={[]}
          selectedAreaPath={selectedAreaPath || ""}
          //Todo improve the project name logic here
          project={selectedProject}
          teamId={""}
          assigneeId={assigneeId || ""}
          showTeams={false}
          useAdvancedMode={props.useAdvancedMode} // Pass useAdvancedMode prop
          projectWorkItemData={props.projectWorkItemData}
          usersData={props.usersData}
          capexOptions={props.capexOptions}
          govProjectOptions={props.govProjectOptions}
          priorityDriverOptions={props.priorityDriverOptions}
          demoOptions={props.demoOptions}
          productOptions={props.productOptions}
          investmentClassOptions={props.investmentClassOptions}
          azureDevOpsService={devOpsService}
          iterationsData={props.iterationsData}
          multiSelectMode={multiSelectMode}
          onMultiSelectChange={(count, editHandler) => {
            setSelectedTicketsCount(count);
            setOnEditMultiSelect(() => editHandler);
          }}
          onSetRefresh={() => {
            const d: Date = new Date();
            setRefresh(d);
          } }
          onUpdateTicket={async (ticketId: number, updatedFields: Record<string, any>) => {
            if (devOpsService) {
              await devOpsService.updateTicket(ticketId, updatedFields);
            }
          } }
          onMoveAndExtendIteration={async (selectedTicket: number, move: number, extend: number) => {
            if (devOpsService) {
              await devOpsService.moveAndExtendTicketIteration(selectedTicket, move, extend);
            }
          } }
          RemoveFromTimeline={async (ticketId: number) => {
            if (devOpsService) {
              await devOpsService.removeTicketFromTimeline(ticketId);
            }
          } }

          AddPlanningTask={async (selectedTicket: Ticket, spec: string) => {
            if (devOpsService) {
              await devOpsService.addPlanningTask(selectedTicket, spec, selectedProject, "Initiative");
            }
          } }
          createTicket={function (project: string, ticketType: string, fields: Record<string, any>, parentUrl : string): Promise<any> {
            if (devOpsService) {
              return devOpsService.createTicket(project, ticketType, fields, parentUrl);
            }
            return Promise.reject(new Error('DevOps service not available'));
          } }        
          FindTicket={async (ticketId: string) => {
            if (devOpsService) {
              return await devOpsService.fetchWorkItemById(Number(ticketId));
            }
            return null;
          } }
          AddTicketToSprint={async (ticketId: string, startDate: string, endDate: string) => {
            if (devOpsService) {
              return await devOpsService.addTicketToSprint(ticketId, startDate, endDate);
            }
            return null;
          } }
        />

      </>
    );
  } else {
    return <LoadingSpinner />;
  }
};

export default TeamPlanningPage;
